import * as signalR from "@microsoft/signalr";
import { useOrderStore } from "@/stores/OrderStore";

let connection = null;
let messageHandlers = [];

// branch registration state
let registeredBranchId = null;
let isBranchRegistered = false;

/**
 * Starts the SignalR realtime connection.
 * Must be called AFTER login (JWT available) AND branchId is known.
 *
 * @param {Function} getToken - function that returns current JWT
 * @param {Function} getBranchContext - function that returns { branchId, lastUpdatedOn? }
 */
export function startRealtime(getToken, getBranchContext) {
  if (!getBranchContext) {
    throw new Error("SignalR: getBranchContext is required");
  }

  if (connection) {
    console.log("SignalR: Connection already exists, skipping start");
    return connection;
  }

  console.log("SignalR: Starting new connection...");
  const token = getToken();
  console.log("SignalR: Token available:", !!token);

  connection = new signalR.HubConnectionBuilder()
    .withUrl("/hubs/pos", {
      accessTokenFactory: () => {
        const t = getToken();
        console.log("SignalR: Providing token to handshake:", !!t);
        return t;
      }
    })
    .withAutomaticReconnect()
    .build();

  connection.on("message", msg => {
    const text = typeof msg === "string" ? msg : JSON.stringify(msg, null, 2);
    console.log("SignalR: Received message:", text);
    // alert(text);
    updateItem(msg)

    messageHandlers.forEach(h => h(msg));
  });

  connection.onreconnecting(err => {
    console.warn("SignalR: Reconnecting...", err);
  });

  connection.onreconnected(() => {
    console.log("SignalR: Reconnected");
    // SignalR groups are not guaranteed after reconnect -> re-register
    isBranchRegistered = false;
    registerBranch(getBranchContext);
  });

  connection.onclose(err => {
    console.warn("SignalR: Connection closed", err);
    isBranchRegistered = false;
  });

  connection
    .start()
    .then(() => {
      console.log("SignalR: ✅ Connected successfully");
      registerBranch(getBranchContext);
    })
    .catch(err => console.error("SignalR: ❌ Failed to connect", err));

  return connection;
}

/**
 * Registers this connection to a branch group.
 * Requires server hub method: RegisterBranches(IEnumerable<Guid> branchIds, string lastUpdatedOn)
 */
function registerBranch(getBranchContext) {
  console.log("SignalR: registerBranch() called");

  if (!connection) {
    console.warn("SignalR: ⚠️ Cannot register - no connection");
    return;
  }

  const ctx = getBranchContext?.();
  console.log("SignalR: branch context =", ctx);

  const branchId = ctx?.branchId;
  const lastUpdatedOn = ctx?.lastUpdatedOn;

  if (!branchId) {
    console.warn("SignalR: ⚠️ Cannot register - missing branchId in context");
    return;
  }

  // Avoid re-register spam if already registered with same branch
  if (isBranchRegistered && registeredBranchId === branchId) {
    console.log("SignalR: Branch already registered, skipping");
    return;
  }

  // Convert lastUpdatedOn to ISO string if it exists
  const lastUpdatedOnISO = lastUpdatedOn 
    ? (lastUpdatedOn instanceof Date ? lastUpdatedOn.toISOString() : lastUpdatedOn)
    : null;

  console.log("SignalR: invoking RegisterBranches with", {
    branchIds: branchId,
    lastUpdatedOn: lastUpdatedOnISO
  });

  connection.invoke("RegisterBranches", branchId, lastUpdatedOnISO)
    .then(() => {
      registeredBranchId = branchId;
      isBranchRegistered = true;
      console.log("SignalR: ✅ branch registered");
    })
    .catch(err => {
      console.error("SignalR: ❌ RegisterBranches failed", err);
    });
}

/**
 * Stops the SignalR connection and cleans up state.
 */
export function stopRealtime() {
  if (!connection) {
    console.log("SignalR: No connection to stop");
    return;
  }

  console.log("SignalR: Stopping connection...");
  connection.stop();
  connection = null;
  messageHandlers = [];

  registeredBranchId = null;
  isBranchRegistered = false;

  console.log("SignalR: Connection stopped");
}

/**
 * Optional helper: call this if branch changes while app is running.
 * Example: user switches branch without full logout.
 */
export async function updateBranchRegistration(getBranchContext) {
  if (!connection) {
    console.warn("SignalR: ⚠️ Cannot update registration - no connection");
    return;
  }

  isBranchRegistered = false;
  registerBranch(getBranchContext);
}

/**
 * Registers a handler for realtime messages.
 *
 * @param {Function} handler
 */
export function onRealtimeMessage(handler) {
  messageHandlers.push(handler);

  return () => {
    messageHandlers = messageHandlers.filter(h => h !== handler);
  };
}

/**
 * Checks if SignalR is currently connected.
 */
export function isRealtimeConnected() {
  return connection !== null;
}

// function to update order item based on realtime message
// passed payload to orderstore
function updateItem(msg) {
  const payload = msg?.payload;
  if (!payload) {
    return;
  }

  const orderStore = useOrderStore();
  const didUpdate = orderStore.applyRealtimeItemUpdate(payload);
  if (didUpdate) {
    orderStore.saveOrderItemsToLocalStorage();
  }
}